import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMnoIpRange,
  deleteMnoIpRange,
  getMnoIpRange,
  listMnoIpRanges,
  listMnoOperators,
  updateMnoIpRange,
  type MnoIpRangeFilters,
} from '@/lib/api/mno-ip-ranges';
import type { CreateMnoIpRangeInput, UpdateMnoIpRangeInput } from '@/types/mno-ip-range';
import { mnoIpRangeKey } from '@/types/mno-ip-range';

export const mnoIpRangeKeys = {
  all: ['mno-ip-ranges'] as const,
  lists: () => [...mnoIpRangeKeys.all, 'list'] as const,
  list: (filters?: MnoIpRangeFilters) => [...mnoIpRangeKeys.lists(), filters ?? {}] as const,
  details: () => [...mnoIpRangeKeys.all, 'detail'] as const,
  detail: (operatorId: string, countryCode: string) =>
    [...mnoIpRangeKeys.details(), mnoIpRangeKey(operatorId, countryCode)] as const,
  operators: () => ['mno-operators'] as const,
};

export function useMnoIpRanges(filters?: MnoIpRangeFilters) {
  return useQuery({
    queryKey: mnoIpRangeKeys.list(filters),
    queryFn: () => listMnoIpRanges(filters),
    staleTime: 30_000,
  });
}

export function useMnoIpRange(operatorId: string | undefined, countryCode: string | undefined) {
  return useQuery({
    queryKey: mnoIpRangeKeys.detail(operatorId ?? '', countryCode ?? ''),
    queryFn: () => getMnoIpRange(operatorId!, countryCode!),
    enabled: !!operatorId && !!countryCode,
    staleTime: 30_000,
  });
}

export function useMnoOperators() {
  return useQuery({
    queryKey: mnoIpRangeKeys.operators(),
    queryFn: listMnoOperators,
    staleTime: 5 * 60_000,
  });
}

export function useCreateMnoIpRange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMnoIpRangeInput) => createMnoIpRange(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mnoIpRangeKeys.lists() });
    },
  });
}

export function useUpdateMnoIpRange(operatorId: string, countryCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMnoIpRangeInput) => updateMnoIpRange(operatorId, countryCode, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mnoIpRangeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: mnoIpRangeKeys.detail(operatorId, countryCode) });
    },
  });
}

export function useDeleteMnoIpRange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ operatorId, countryCode }: { operatorId: string; countryCode: string }) =>
      deleteMnoIpRange(operatorId, countryCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mnoIpRangeKeys.lists() });
    },
  });
}
